import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material'
import { ExpandMore, CheckCircle, CheckCircleOutline } from '@mui/icons-material'
import { ThemeButton } from '../../../ui'

const columns = [
  {
    id: 'name',
    label: 'pages.eeii.detail.mailingConfig.columns.name',
    width: '30%',
  },
  {
    id: 'description',
    label: 'pages.eeii.detail.mailingConfig.columns.description',
    width: '60%',
  },
  {
    id: 'status',
    label: 'pages.eeii.detail.mailingConfig.columns.status',
    type: 'status',
    width: '10%',
  },
]

export const MailingView = ({ close, preferencias = [] }) => {
  const { formatMessage } = useIntl()

  const categories = useMemo(() => {
    const groupedByCategory = {}

    preferencias.forEach((pref) => {
      const categoryName = pref.category || 'Sin categoría'

      if (!groupedByCategory[categoryName]) {
        groupedByCategory[categoryName] = {
          id: categoryName,
          name: categoryName,
          applications: [],
        }
      }

      const application = {
        id: pref.id,
        name: pref.email_name,
        description: pref.email_description,
        required: pref.necessary,
        status: pref.send,
      }

      groupedByCategory[categoryName].applications.push(application)
    })

    return Object.values(groupedByCategory).filter((category) => category.applications.length > 0)
  }, [preferencias])

  const getCategoryStats = (category) => {
    const subscribedCount = category.applications.filter((app) => app.status).length
    const totalCount = category.applications.length
    return { subscribedCount, totalCount }
  }

  if (categories.length === 0) {
    return (
      <Box p={4}>
        <Typography variant="body1" align="center" color="textSecondary" paragraph>
          {formatMessage({ id: 'pages.eeii.detail.mailingConfig.noData' })}
        </Typography>
        <Box display="flex" justifyContent="center">
          <ThemeButton onClick={close} color="primary" labelM="lg">
            {formatMessage({ id: 'global.close' })}
          </ThemeButton>
        </Box>
      </Box>
    )
  }

  return (
    <Box p={4}>
      <Box mb={3}>
        <Typography variant="h6" color="primary" gutterBottom>
          {formatMessage({ id: 'pages.eeii.detail.mailingConfig.title' })}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {formatMessage({ id: 'pages.eeii.detail.mailingConfig.subtitle' })}
        </Typography>
      </Box>

      {categories.map((category) => {
        const { subscribedCount, totalCount } = getCategoryStats(category)

        return (
          <Accordion key={category.id} defaultExpanded={categories.length === 1}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                backgroundColor: '#f5f5f5',
                '&:hover': {
                  backgroundColor: '#eeeeee',
                },
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Typography variant="subtitle1" fontWeight="bold">
                  {category.name}
                </Typography>
                <Box display="flex" alignItems="center" mr={2}>
                  <Chip
                    label={`${subscribedCount}/${totalCount} suscritos`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        style={{
                          paddingBottom: 8,
                          borderBottomWidth: '2px',
                          width: column.width,
                          textAlign: column.type === 'status' ? 'center' : 'left',
                        }}
                      >
                        <b>{formatMessage({ id: column.label })}</b>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {category.applications.map((row) => (
                    <TableRow key={row.id}>
                      {columns.map((column) => {
                        switch (column.type) {
                          case 'status':
                            return (
                              <TableCell
                                style={{
                                  paddingBottom: 8,
                                  paddingTop: 8,
                                  width: column.width,
                                  textAlign: 'center',
                                }}
                                key={column.id}
                              >
                                <Box
                                  display="flex"
                                  flexDirection="column"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  {row.status ? (
                                    <CheckCircle color="primary" sx={{ fontSize: 24 }} />
                                  ) : (
                                    <CheckCircleOutline sx={{ fontSize: 24, color: '#ccc' }} />
                                  )}
                                  {row.required && (
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                      textAlign="center"
                                      mt={0.5}
                                    >
                                      (
                                      {formatMessage({
                                        id: 'pages.eeii.detail.mailingConfig.required',
                                      })}
                                      )
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                            )
                          default:
                            return (
                              <TableCell
                                style={{
                                  paddingBottom: 8,
                                  paddingTop: 8,
                                  width: column.width,
                                  wordWrap: 'break-word',
                                }}
                                key={column.id}
                              >
                                {row[column.id] || '--'}
                              </TableCell>
                            )
                        }
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        )
      })}

      <Box display="flex" justifyContent="center" mt={3}>
        <ThemeButton onClick={close} color="primary" labelM="lg">
          {formatMessage({ id: 'global.close' })}
        </ThemeButton>
      </Box>
    </Box>
  )
}
